import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import { useAsyncResource } from '@/shared/hooks/use-async-resource';
import { agreementsPlatformApi } from '@/modules/agreements/services/platform-api';
import {
  executionStatusLabel,
  executionStatusTone,
  isForbidden,
  requiredActionLabel,
} from '@/modules/agreements/shared';
import { ExecutionSummary } from './parts/execution-summary';
import { EvidenceSection } from './parts/evidence-section';

/**
 * One execution, seen by the platform.
 *
 * The platform can READ an execution and its restricted evidence. It cannot complete an
 * execution on a subject's behalf — there is no control here that does so.
 */
export function ExecutionDetailPage(props: { executionId: string }): ReactNode {
  const load = useCallback(
    () => agreementsPlatformApi.getExecution(props.executionId),
    [props.executionId],
  );
  const { data, error, isLoading } = useAsyncResource(load);
  const execution = data;

  return (
    <AppDetailLayout
      headerVariant="accent"
      title={execution?.executionRef ?? 'Agreement execution'}
      subtitle={execution ? requiredActionLabel(execution.requiredAction) : undefined}
      breadcrumbs={[
        { label: 'Platform', href: '/platform/tenants' },
        { label: 'Agreements', href: '/platform/agreements/documents' },
        { label: 'Executions', href: '/platform/agreements/executions' },
        { label: execution?.executionRef ?? 'Execution' },
      ]}
      isLoading={isLoading}
      isDenied={isForbidden(error)}
      error={error}
      status={
        execution
          ? {
              label: executionStatusLabel(execution.status),
              tone: executionStatusTone(execution.status),
            }
          : undefined
      }
      sections={[
        {
          id: 'summary',
          label: 'Summary',
          content: execution ? (
            <ExecutionSummary
              subjectKind={execution.subjectKind}
              subjectId={execution.subjectId}
              signerName={execution.signerName}
              signingMethod={execution.signingMethod}
              completedAt={execution.completedAt}
              contentHash={execution.contentHash}
            />
          ) : null,
        },
        {
          id: 'evidence',
          label: 'Evidence (restricted)',
          content: <EvidenceSection executionId={props.executionId} />,
        },
      ]}
    />
  );
}
